<!-- ExportOptionsModal.vue -->
<template>
  <div v-if="show" class="modal-overlay" @click.self="closeModal">
    <div class="modal-container">
      <div class="modal-header">
        <h2>📄 Export Compliance Report</h2>
        <button class="close-btn" @click="closeModal">✕</button>
      </div>
      <!-- Notification Banner -->
      <transition name="slide-down">
        <div
          v-if="notification.show"
          :class="['notification-banner', notification.type]"
        >
          <div class="notification-icon">
            <span v-if="notification.type === 'success'">✅</span>
            <span v-else>⚠️</span>
          </div>
          <div class="notification-message">{{ notification.message }}</div>
          <button class="notification-close" @click="notification.show = false">
            ✕
          </button>
        </div>
      </transition>
      <div class="modal-body">
        <!-- Company Banner Section -->
        <div class="section">
          <h3>🎨 Company Branding</h3>
          <div class="banner-upload">
            <div v-if="!bannerPreview" class="upload-area">
              <input
                type="file"
                ref="bannerInput"
                accept="image/png,image/jpeg,image/jpg"
                @change="handleBannerUpload"
                style="display: none"
              />
              <div
                class="upload-placeholder"
                @click="$refs.bannerInput.click()"
              >
                <div class="upload-icon">🖼️</div>
                <p>Click to upload company banner</p>
                <span class="upload-hint"
                  >PNG or JPG, max 2MB, 1200x300px recommended</span
                >
              </div>
            </div>

            <div v-else class="banner-preview">
              <img :src="bannerPreview" alt="Banner preview" />
              <button class="remove-banner-btn" @click="removeBanner">
                Remove Banner
              </button>
            </div>
          </div>
        </div>

        <!-- Report Sections -->
        <div class="section">
          <h3>📊 Report Sections</h3>
          <div class="checkbox-group">
            <label class="checkbox-item">
              <input type="checkbox" v-model="options.includeSummary" />
              <span class="checkbox-label">
                <strong>Executive Summary</strong>
                <small
                  >Compliance score, overall statistics, and key metrics</small
                >
              </span>
            </label>

            <label class="checkbox-item">
              <input
                type="checkbox"
                v-model="options.includeCategoryBreakdown"
              />
              <span class="checkbox-label">
                <strong>Category Breakdown</strong>
                <small>Deviations grouped by policy category</small>
              </span>
            </label>

            <label class="checkbox-item">
              <input
                type="checkbox"
                v-model="options.includeDetailedDeviations"
              />
              <span class="checkbox-label">
                <strong>Detailed Deviations</strong>
                <small>Field-level differences for each policy</small>
              </span>
            </label>
          </div>
        </div>

        <!-- Severity Filter -->
        <div class="section">
          <h3>⚠️ Severity Filter</h3>
          <div class="radio-group">
            <label class="radio-item">
              <input
                type="radio"
                value="all"
                v-model="options.severityFilter"
              />
              <span class="radio-label">
                All Severities
                <span class="count-badge">{{ totalDeviations }}</span>
              </span>
            </label>

            <label class="radio-item">
              <input
                type="radio"
                value="criticalHigh"
                v-model="options.severityFilter"
              />
              <span class="radio-label">
                Critical & High Only
                <span class="count-badge critical">{{
                  criticalHighCount
                }}</span>
              </span>
            </label>

            <label class="radio-item">
              <input
                type="radio"
                value="critical"
                v-model="options.severityFilter"
              />
              <span class="radio-label">
                Critical Only
                <span class="count-badge critical">{{ criticalCount }}</span>
              </span>
            </label>

            <label class="radio-item">
              <input
                type="radio"
                value="high"
                v-model="options.severityFilter"
              />
              <span class="radio-label">
                High Only
                <span class="count-badge high">{{ highCount }}</span>
              </span>
            </label>

            <label class="radio-item">
              <input
                type="radio"
                value="medium"
                v-model="options.severityFilter"
              />
              <span class="radio-label">
                Medium Only
                <span class="count-badge medium">{{ mediumCount }}</span>
              </span>
            </label>
          </div>
        </div>

        <!-- Category Filter -->
        <div class="section">
          <h3>📁 Category Filter</h3>
          <div class="category-filter-controls">
            <button
              class="filter-btn"
              @click="selectAllCategories"
              :disabled="allCategoriesSelected"
            >
              Select All
            </button>
            <button
              class="filter-btn"
              @click="deselectAllCategories"
              :disabled="!anyCategoriesSelected"
            >
              Deselect All
            </button>
          </div>

          <div class="checkbox-group categories">
            <label
              v-for="category in availableCategories"
              :key="category.name"
              class="checkbox-item"
            >
              <input
                type="checkbox"
                :value="category.name"
                v-model="options.categories"
              />
              <span class="checkbox-label">
                {{ category.name }}
                <span class="count-badge">{{ category.count }}</span>
              </span>
            </label>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <div class="export-summary">
          <span class="summary-text">
            {{ exportSummary }}
          </span>
        </div>
        <div class="action-buttons">
          <button class="btn-secondary" @click="closeModal">Cancel</button>
          <button
            class="btn-primary"
            @click="handleExport"
            :disabled="!canExport || exporting"
          >
            <span v-if="exporting"> ⏳ Generating PDF... </span>
            <span v-else> 📥 Export PDF </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import pdfExportService from "@/services/pdf-export-service";

export default {
  name: "ExportOptionsModal",

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    complianceData: {
      type: Object,
      required: true,
    },
    groupedDeviations: {
      type: Array,
      required: true,
    },
    clientCode: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      options: {
        includeSummary: true,
        includeCategoryBreakdown: true,
        includeDetailedDeviations: true,
        severityFilter: "all",
        categories: [],
      },
      bannerPreview: null,
      bannerData: null,
      exporting: false,
      notification: {
        show: false,
        type: "error",
        message: "",
      },
    };
  },

  computed: {
    availableCategories() {
      return this.groupedDeviations.map((group) => ({
        name: group.category,
        count: group.deviationCount,
      }));
    },

    totalDeviations() {
      return this.groupedDeviations.reduce(
        (sum, group) => sum + group.deviationCount,
        0
      );
    },

    criticalCount() {
      return this.groupedDeviations.reduce(
        (sum, group) => sum + (group.criticalCount || 0),
        0
      );
    },

    highCount() {
      return this.groupedDeviations.reduce(
        (sum, group) => sum + (group.highCount || 0),
        0
      );
    },

    mediumCount() {
      return this.groupedDeviations.reduce(
        (sum, group) => sum + (group.mediumCount || 0),
        0
      );
    },

    criticalHighCount() {
      return this.criticalCount + this.highCount;
    },

    allCategoriesSelected() {
      return this.options.categories.length === this.availableCategories.length;
    },

    anyCategoriesSelected() {
      return this.options.categories.length > 0;
    },

    canExport() {
      // At least one section must be selected
      const hasSection =
        this.options.includeSummary ||
        this.options.includeCategoryBreakdown ||
        this.options.includeDetailedDeviations;

      // At least one category must be selected (or all if none explicitly chosen)
      const hasCategories =
        this.options.categories.length > 0 ||
        this.options.severityFilter === "all";

      return hasSection && hasCategories;
    },

    exportSummary() {
      const sections = [];
      if (this.options.includeSummary) sections.push("Summary");
      if (this.options.includeCategoryBreakdown)
        sections.push("Category Breakdown");
      if (this.options.includeDetailedDeviations)
        sections.push("Detailed Deviations");

      const categoryCount =
        this.options.categories.length || this.availableCategories.length;
      const categoryText = categoryCount === 1 ? "category" : "categories";

      return `Exporting ${sections.join(
        ", "
      )} for ${categoryCount} ${categoryText}`;
    },
  },

  watch: {
    show(newVal) {
      if (newVal) {
        // Initialize with all categories selected
        this.selectAllCategories();
      }
    },
  },

  methods: {
    showNotification(message, type = "error") {
      this.notification.message = message;
      this.notification.type = type;
      this.notification.show = true;

      setTimeout(() => {
        this.notification.show = false;
      }, 6000);
    },
    async handleBannerUpload(event) {
      const file = event.target.files[0];

      if (!file) return;

      // Validate file type
      if (!file.type.match(/image\/(png|jpeg|jpg)/)) {
        this.showNotification("Please upload a PNG or JPG image");
        return;
      }

      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        this.showNotification("Image size must be less than 2MB");
        return;
      }

      // Read file and create preview
      const reader = new FileReader();

      reader.onload = (e) => {
        this.bannerPreview = e.target.result;
        this.bannerData = e.target.result;

        // Store in localStorage for persistence
        localStorage.setItem("companyBanner", e.target.result);
      };

      reader.readAsDataURL(file);
    },

    removeBanner() {
      this.bannerPreview = null;
      this.bannerData = null;
      localStorage.removeItem("companyBanner");

      if (this.$refs.bannerInput) {
        this.$refs.bannerInput.value = "";
      }
    },

    selectAllCategories() {
      this.options.categories = this.availableCategories.map((cat) => cat.name);
    },

    deselectAllCategories() {
      this.options.categories = [];
    },

    async handleExport() {
      if (!this.canExport || this.exporting) return;

      this.exporting = true;

      try {
        const exportOptions = {
          complianceData: this.complianceData,
          groupedDeviations: this.groupedDeviations,
          clientCode: this.clientCode,
          filters: {
            ...this.options,
            categories:
              this.options.categories.length > 0
                ? this.options.categories
                : this.availableCategories.map((c) => c.name),
          },
          bannerImage: this.bannerData,
        };

        const filename = await pdfExportService.exportToPDF(exportOptions);

        this.$emit("export-complete", filename);

        // Show success notification
        this.showNotification(
          `Successfully exported: ${filename}`,
          "success"
        );

        // Close modal after brief delay
        setTimeout(() => {
          this.closeModal();
        }, 1500);
      } catch (error) {
        console.error("Export failed:", error);
        this.showNotification("Failed to export PDF. Please try again.");
      } finally {
        this.exporting = false;
      }
    },

    closeModal() {
      if (!this.exporting) {
        this.$emit("close");
      }
    },

    loadStoredBanner() {
      const storedBanner = localStorage.getItem("companyBanner");
      if (storedBanner) {
        this.bannerPreview = storedBanner;
        this.bannerData = storedBanner;
      }
    },
  },

  mounted() {
    this.loadStoredBanner();
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 1000px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 28px;
  border-bottom: 2px solid #e0e0e0;
}

.modal-header h2 {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #1a1a1a;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f0f0f0;
  color: #333;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px 28px;
}

.section {
  margin-bottom: 28px;
}

.section:last-child {
  margin-bottom: 0;
}

.section h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
}

/* Banner Upload */
.banner-upload {
  border: 2px dashed #d0d0d0;
  border-radius: 8px;
  overflow: hidden;
}

.upload-area {
  padding: 20px;
}

.upload-placeholder {
  text-align: center;
  cursor: pointer;
  padding: 20px;
  transition: all 0.2s;
}

.upload-placeholder:hover {
  background: #f8f9fa;
}

.upload-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.upload-placeholder p {
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.upload-hint {
  font-size: 12px;
  color: #999;
}

.banner-preview {
  position: relative;
}

.banner-preview img {
  width: 100%;
  height: auto;
  display: block;
}

.remove-banner-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(220, 53, 69, 0.95);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.remove-banner-btn:hover {
  background: #dc3545;
}

/* Checkbox Group */
.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.checkbox-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  padding: 12px;
  border-radius: 6px;
  transition: all 0.2s;
}

.checkbox-item:hover {
  background: #f8f9fa;
}

.checkbox-item input[type="checkbox"] {
  margin-top: 2px;
  width: 18px;
  height: 18px;
  cursor: pointer;
  flex-shrink: 0;
}

.checkbox-label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.checkbox-label strong {
  font-size: 14px;
  color: #1a1a1a;
}

.checkbox-label small {
  font-size: 12px;
  color: #666;
}

/* Radio Group */
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.radio-item {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 10px 12px;
  border-radius: 6px;
  transition: all 0.2s;
}

.radio-item:hover {
  background: #f8f9fa;
}

.radio-item input[type="radio"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  flex-shrink: 0;
}

.radio-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  font-size: 14px;
  color: #1a1a1a;
}

.count-badge {
  background: #e0e0e0;
  color: #666;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
}

.count-badge.critical {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

.count-badge.high {
  background: rgba(253, 126, 20, 0.1);
  color: #fd7e14;
}

.count-badge.medium {
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
}

/* Category Filter */
.category-filter-controls {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.filter-btn {
  padding: 6px 12px;
  background: #f0f0f0;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-btn:hover:not(:disabled) {
  background: #e0e0e0;
}

.filter-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.checkbox-group.categories {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 8px;
}

/* Modal Footer */
.modal-footer {
  padding: 20px 28px;
  border-top: 2px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.export-summary {
  flex: 1;
}

.summary-text {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-secondary,
.btn-primary {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary {
  background: #f0f0f0;
  color: #333;
}

.btn-secondary:hover {
  background: #e0e0e0;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5568d3;
}

.btn-primary:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.notification-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 28px;
  border-bottom: 2px solid;
  animation: slideDown 0.3s ease;
}

.notification-banner.success {
  background: #d1fae5;
  border-color: #10b981;
  color: #065f46;
}

.notification-banner.error {
  background: #fee2e2;
  border-color: #ef4444;
  color: #991b1b;
}

.notification-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.notification-message {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
}

.notification-close {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  transition: all 0.2s;
  opacity: 0.7;
}

.notification-close:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.1);
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
